/**
 * CweNeutralizationProofView — CWE Top 100 proof summary backed by the canonical registry.
 *
 * Replaces the Python HTML generator with a reusable React component that
 * renders the same registry-backed structural proof data in the TypeScript
 * presentation layer.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

import React, { useMemo, useState } from "react";
import { GENERATOR_COLOR, GRAY, SAFE, WARN } from "../design-system/tokens.js";
import { useFacetSelection } from "../hooks/useFacetSelection.js";
import { Card } from "./Card.js";
import { createCweEntryColumns } from "./cwe-neutralization-proof/table-columns.js";
import type {
  CweNeutralizationEntry,
  CweNeutralizationFamily,
  CweNeutralizationMap,
  CweNeutralizationProofViewProps,
} from "./cwe-neutralization-proof/types.js";
import {
  cweEntrySearchText,
  THEOREM_ORDER,
  theoremName,
} from "./cwe-neutralization-proof/utils.js";
import { VirtualizedDataTable } from "./data/index.js";
import { EmptyState } from "./EmptyState.js";
import { FilterBar, type FilterFacet } from "./FilterBar.js";
import { MarkdownPanel } from "./MarkdownPanel.js";
import { Pill } from "./Pill.js";
import {
  GENERATOR_NAME,
  GENERATOR_ORDER,
  normalizeQuery,
  theoremPresentation,
  type CrossmapGeneratorCode,
} from "./proofViewShared.js";
import { Section } from "./Section.js";

export type {
  CweNeutralizationEntry,
  CweNeutralizationFamily,
  CweNeutralizationMap,
  CweNeutralizationProofViewProps,
} from "./cwe-neutralization-proof/types.js";

interface IndexedCweEntry {
  readonly entry: CweNeutralizationEntry;
  readonly searchText: string;
}

interface IndexedCweFamily {
  readonly family: CweNeutralizationFamily;
  readonly entries: readonly IndexedCweEntry[];
}

interface VisibleCweFamily {
  readonly family: CweNeutralizationFamily;
  readonly entries: readonly CweNeutralizationEntry[];
}

/** Render the canonical CWE neutralization proof summary. */
export const CweNeutralizationProofView = React.memo(function CweNeutralizationProofView({
  data,
  initialSearch = "",
}: CweNeutralizationProofViewProps): React.JSX.Element {
  const [search, setSearch] = useState(initialSearch);
  const generatorSelection = useFacetSelection<CrossmapGeneratorCode>();
  const query = normalizeQuery(search);

  const totalEntries = data.claim.total_cwes;

  const indexedFamilies = useMemo<readonly IndexedCweFamily[]>(
    () =>
      data.families.map((family) => ({
        family,
        entries: family.entries.map((entry) => ({
          entry,
          searchText: cweEntrySearchText(entry),
        })),
      })),
    [data.families],
  );

  const generatorFacets = useMemo<readonly FilterFacet<CrossmapGeneratorCode>[]>(
    () =>
      GENERATOR_ORDER.map((generator) => ({
        id: generator,
        label: `${generator} ${GENERATOR_NAME[generator]}`,
        count: data.aggregate.generator_distribution[generator]?.total ?? 0,
        color: GENERATOR_COLOR[generator].primary,
      })),
    [data.aggregate.generator_distribution],
  );

  const { visibleFamilies, visibleEntryCount } = useMemo(() => {
    const families: VisibleCweFamily[] = [];
    let entryCount = 0;

    for (const { family, entries: indexedEntries } of indexedFamilies) {
      const entries: CweNeutralizationEntry[] = [];
      for (const { entry, searchText } of indexedEntries) {
        const matchesGenerator =
          generatorSelection.active.size === 0 ||
          entry.generators.some((generator) => generatorSelection.active.has(generator));
        if (matchesGenerator && (query === "" || searchText.includes(query))) {
          entries.push(entry);
        }
      }
      if (entries.length > 0) {
        entryCount += entries.length;
        families.push({ family, entries });
      }
    }

    return { visibleEntryCount: entryCount, visibleFamilies: families };
  }, [generatorSelection.active, indexedFamilies, query]);

  const entryColumns = useMemo(createCweEntryColumns, []);

  return (
    <div
      style={{
        color: GRAY[900],
        display: "grid",
        gap: "1.5rem",
        fontFamily: "Inter, system-ui, sans-serif",
      }}
    >
      <header>
        <h1 style={{ margin: 0, fontSize: "2rem", lineHeight: 1.15 }}>
          CWE Top 100 Neutralization Proof
        </h1>
        <MarkdownPanel style={{ color: GRAY[600], marginTop: "0.5rem" }}>
          {
            "Canonical registry view of the **Top 100 CWE entries** and their structural neutralization mapping."
          }
        </MarkdownPanel>
      </header>

      <Card accent={SAFE[500]}>
        <div
          style={{
            fontSize: "0.75rem",
            fontWeight: 700,
            color: SAFE[700],
            letterSpacing: "0.06em",
          }}
        >
          COVERAGE CLAIM
        </div>
        <MarkdownPanel style={{ marginTop: "0.5rem" }}>{data.claim.statement}</MarkdownPanel>
      </Card>

      <div style={{ display: "flex", flexWrap: "wrap", gap: "0.75rem" }}>
        <Card style={{ flex: "1 1 10rem" }}>
          <div style={{ fontSize: "1.875rem", fontWeight: 700, color: SAFE[700], lineHeight: 1.1 }}>
            {data.claim.regime1_count}
          </div>
          <div style={{ fontSize: "0.8125rem", color: GRAY[600], marginTop: "0.4rem" }}>
            Regime 1 CWEs
          </div>
        </Card>
        <Card style={{ flex: "1 1 10rem" }}>
          <div
            style={{
              fontSize: "1.875rem",
              fontWeight: 700,
              color: GENERATOR_COLOR.G2.primary,
              lineHeight: 1.1,
            }}
          >
            {data.claim.regime1_percentage}%
          </div>
          <div style={{ fontSize: "0.8125rem", color: GRAY[600], marginTop: "0.4rem" }}>
            Regime 1 coverage
          </div>
        </Card>
        <Card style={{ flex: "1 1 10rem" }}>
          <div style={{ fontSize: "1.875rem", fontWeight: 700, color: "#7c3aed", lineHeight: 1.1 }}>
            {data.aggregate.generator_distribution.G2?.total ?? 0}
          </div>
          <div style={{ fontSize: "0.8125rem", color: GRAY[600], marginTop: "0.4rem" }}>
            CWEs touching G2
          </div>
        </Card>
        <Card style={{ flex: "1 1 10rem" }}>
          <div style={{ fontSize: "1.875rem", fontWeight: 700, color: WARN[700], lineHeight: 1.1 }}>
            {data.aggregate.reclassified_entries.count}
          </div>
          <div style={{ fontSize: "0.8125rem", color: GRAY[600], marginTop: "0.4rem" }}>
            Reclassified entries
          </div>
        </Card>
      </div>

      <Section
        title="Generator distribution"
        description="Primary and secondary appearances across the Top 100 registry."
      >
        <Card>
          <div style={{ display: "grid", gap: "0.75rem" }}>
            {GENERATOR_ORDER.map((generator) => {
              const bar = data.aggregate.generator_distribution[generator];
              const percentage = bar?.percentage ?? 0;
              return (
                <div
                  key={generator}
                  style={{
                    display: "grid",
                    gap: "0.5rem",
                    gridTemplateColumns: "9rem 1fr 10rem",
                    alignItems: "center",
                  }}
                >
                  <div
                    style={{
                      color: GENERATOR_COLOR[generator].primary,
                      fontWeight: 600,
                      fontSize: "0.875rem",
                    }}
                  >
                    {generator} {GENERATOR_NAME[generator]}
                  </div>
                  <div
                    style={{
                      backgroundColor: GRAY[100],
                      borderRadius: "9999px",
                      overflow: "hidden",
                      minHeight: "1.5rem",
                    }}
                  >
                    <div
                      style={{
                        alignItems: "center",
                        backgroundColor: GENERATOR_COLOR[generator].primary,
                        color: "white",
                        display: "flex",
                        fontSize: "0.75rem",
                        fontWeight: 700,
                        height: "100%",
                        minHeight: "1.5rem",
                        paddingLeft: percentage > 0 ? "0.5rem" : 0,
                        width: `${percentage}%`,
                      }}
                    >
                      {percentage}%
                    </div>
                  </div>
                  <div style={{ color: GRAY[700], fontSize: "0.8125rem", textAlign: "right" }}>
                    {bar?.as_primary ?? 0} primary · {bar?.as_secondary ?? 0} secondary
                  </div>
                </div>
              );
            })}
          </div>
        </Card>
      </Section>

      <Section
        title="Theorem coverage"
        description="Primary theorem coverage derived from the aggregate registry."
      >
        <div
          style={{
            display: "grid",
            gap: "0.75rem",
            gridTemplateColumns: "repeat(auto-fit, minmax(14rem, 1fr))",
          }}
        >
          {THEOREM_ORDER.map((theoremCode) => {
            const coverage = data.aggregate.theorem_coverage[theoremCode];
            if (coverage === undefined) return null;
            const theorem = theoremPresentation(theoremCode);
            return (
              <Card key={theoremCode} accent={theorem.color}>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: "0.75rem",
                  }}
                >
                  <div>
                    <div style={{ color: theorem.color, fontSize: "1.05rem", fontWeight: 700 }}>
                      {theorem.label}
                    </div>
                    <div style={{ color: GRAY[600], fontSize: "0.8125rem" }}>{theorem.title}</div>
                  </div>
                  <Pill color={theorem.color} bg={theorem.bg} border={`${theorem.color}33`}>
                    {coverage.primary_cwe_count} primary CWEs
                  </Pill>
                </div>
                {coverage.key_families.length > 0 && (
                  <div
                    style={{
                      display: "flex",
                      flexWrap: "wrap",
                      gap: "0.35rem",
                      marginTop: "0.75rem",
                    }}
                  >
                    {coverage.key_families.map((familyId) => (
                      <Pill key={`${theoremCode}-${familyId}`} tone="neutral" mono>
                        {familyId}
                      </Pill>
                    ))}
                  </div>
                )}
              </Card>
            );
          })}
        </div>
      </Section>

      <Section
        title="CWE families"
        description={`${visibleEntryCount} of ${totalEntries} CWEs match the current filters`}
        action={
          <label style={{ display: "grid", gap: "0.25rem" }}>
            <span style={{ color: GRAY[600], fontSize: "0.75rem", fontWeight: 600 }}>
              Search CWEs
            </span>
            <input
              aria-label="Search CWEs"
              type="search"
              value={search}
              onChange={(event): void => {
                setSearch(event.target.value);
              }}
              placeholder="Search by CWE id, name, or theorem"
              style={{
                border: `1px solid ${GRAY[300]}`,
                borderRadius: "0.375rem",
                color: GRAY[900],
                fontSize: "0.875rem",
                minWidth: "16rem",
                padding: "0.45rem 0.65rem",
              }}
            />
          </label>
        }
      >
        <div style={{ display: "grid", gap: "0.75rem" }}>
          <FilterBar
            facets={generatorFacets}
            active={generatorSelection.active}
            onToggle={generatorSelection.toggle}
            onClear={generatorSelection.clear}
            label="Generators"
          />

          {visibleFamilies.length === 0 ? (
            <EmptyState
              icon="📘"
              title="No CWE entries match the current filters"
              description="Clear the generator filters or broaden the search query."
              action={
                generatorSelection.active.size > 0 || query !== "" ? (
                  <button
                    type="button"
                    onClick={(): void => {
                      generatorSelection.clear();
                      setSearch("");
                    }}
                    style={{
                      background: "transparent",
                      border: "none",
                      color: GENERATOR_COLOR.G2.primary,
                      cursor: "pointer",
                      fontSize: "0.8125rem",
                      fontWeight: 600,
                      textDecoration: "underline",
                    }}
                  >
                    Reset filters
                  </button>
                ) : undefined
              }
            />
          ) : (
            visibleFamilies.map(({ family, entries }) => (
              <Card
                key={family.id}
                accent={
                  family.dominant_generator !== undefined
                    ? GENERATOR_COLOR[family.dominant_generator].primary
                    : GENERATOR_COLOR.G2.primary
                }
              >
                <details open>
                  <summary style={{ cursor: "pointer", fontWeight: 700, listStyle: "none" }}>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        flexWrap: "wrap",
                        gap: "0.5rem",
                      }}
                    >
                      <span>{family.name}</span>
                      <Pill tone="info">{entries.length} entries</Pill>
                      {family.dominant_generator !== undefined && (
                        <Pill
                          color={GENERATOR_COLOR[family.dominant_generator].primary}
                          bg={`${GENERATOR_COLOR[family.dominant_generator].primary}12`}
                          border={`${GENERATOR_COLOR[family.dominant_generator].primary}33`}
                        >
                          Dominant {family.dominant_generator}
                        </Pill>
                      )}
                    </div>
                  </summary>

                  {family.summary !== undefined && (
                    <MarkdownPanel style={{ marginTop: "0.75rem" }}>{family.summary}</MarkdownPanel>
                  )}

                  <div style={{ marginTop: "0.75rem" }}>
                    <VirtualizedDataTable
                      ariaLabel={`${family.name} CWE entries`}
                      columns={entryColumns}
                      data={entries}
                      getRowId={(entry): string => `${family.id}-${entry.cwe_id}`}
                      minWidth="58rem"
                      testId={`cwe-entries-table-${family.id}`}
                    />
                  </div>
                </details>
              </Card>
            ))
          )}
        </div>
      </Section>

      <Card footer={<code>{data.aggregate.reclassified_entries.methodology}</code>}>
        <MarkdownPanel>
          {
            "The registry remains the single source of truth; this component only renders its structure and coverage summary through **React** and the open-source `react-markdown` pipeline."
          }
        </MarkdownPanel>
      </Card>
    </div>
  );
});

export function theoremCoverageSummary(data: CweNeutralizationMap): readonly string[] {
  return THEOREM_ORDER.filter(
    (theoremCode) => data.aggregate.theorem_coverage[theoremCode] !== undefined,
  ).map((theoremCode) => theoremName(theoremCode));
}
